/**
 * The chat transcript's markdown table, plus a "Create dataset" affordance.
 *
 * Cells open full-size through the grid's expand-cell modal (same one the
 * dataset preview uses), so long values aren't stuck truncated in a 200px cell.
 *
 * Below the read-only {@link MarkdownTableGrid} sits a button that materializes
 * the table into a real dataset and swaps the table for a dataset card — i.e.
 * the message now reads as a tool-call that returned an artifact. The actual
 * create/open work comes from {@link useChatTableActions} (a context), so this
 * file stays free of chat-model imports and safe to sit in the Streamdown render
 * chain. The button only appears where that context is provided (a Coco chat).
 */

import React, { useCallback, useState } from 'react';
import { Database } from 'lucide-react';
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  MarkdownTableGrid,
  type MarkdownTableData,
} from '@cotera/client/app/components/ui/data-grid/markdown-table';
import {
  useChatTableActions,
  type ChatTableActions,
  type CreatedDataset,
} from './chat-table-actions';
import { useChatMessageId } from './chat-message-context';
import { Icon } from '@cotera/client/app/components/ui';

type CreateStatus = 'idle' | 'creating' | 'error';

/** Dataset card the table collapses into once created; opens the panel on click. */
const CreatedDatasetCard: React.FC<{
  name: string;
  onOpen: () => void;
}> = ({ name, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    className="my-2 flex w-full items-center gap-3 rounded-md border border-border bg-muted/30 px-3 py-2 text-left transition-colors hover:bg-muted"
  >
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-primary/10 text-primary">
      <Database className="h-4 w-4" />
    </span>
    <span className="flex min-w-0 flex-col">
      <span className="truncate text-sm font-medium text-foreground">
        {name}
      </span>
      <span className="text-xs text-muted-foreground">
        Dataset · click to open
      </span>
    </span>
  </button>
);

const ChatDatasetTableWithActions: React.FC<{
  data: MarkdownTableData;
  actions: ChatTableActions;
}> = ({ data, actions }) => {
  const messageId = useChatMessageId();
  const [status, setStatus] = useState<CreateStatus>('idle');
  const [created, setCreated] = useState<CreatedDataset | null>(null);

  const onCreate = useCallback(async () => {
    setStatus('creating');
    const result = await actions.createDataset(data, messageId);
    if (result === null) {
      setStatus('error');
      return;
    }
    setCreated(result);
    setStatus('idle');
  }, [actions, data, messageId]);

  if (created !== null) {
    return (
      <CreatedDatasetCard
        name={created.name}
        onOpen={() => actions.openDataset(created.id)}
      />
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <MarkdownTableGrid data={data} enableCellDetail />
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          icon="table-cells"
          loading={status === 'creating'}
          onClick={() => void onCreate()}
        >
          Create dataset <Icon icon="plus" />
        </Button>
        {status === 'error' ? (
          <span className="text-xs text-destructive">
            Couldn’t create dataset. Try again.
          </span>
        ) : null}
      </div>
    </div>
  );
};

export const ChatDatasetTable: React.FC<{ data: MarkdownTableData }> = ({
  data,
}) => {
  const actions = useChatTableActions();

  if (actions === null) {
    return <MarkdownTableGrid data={data} enableCellDetail />;
  }

  return <ChatDatasetTableWithActions data={data} actions={actions} />;
};
